// Reservebotten ("Volt på rygmarven"): en nyttefunktion over de lovlige handlinger.
//
//   score = 100·forhindrer_udfald + 20·grøn_delta + 0,3·indtægt − 0,5·pris
//           − 50·ulovlig − 30·dublet + støj(seedet, ±5)
//
// Byggeri vurderes ved at klone state, udføre handlingen og regne flowet igen. Det er dyrt, så kun
// de mest lovende ledninger, opgraderinger og batterier (efter en billig forhåndsvurdering) bliver
// regnet igennem. Botten rører aldrig den rigtige state; den returnerer kun en Intent. Støjen er en
// ren funktion af seed, tick og kandidat, så samme state altid giver samme valg.

import { apply, legalActions } from '../sim/actions';
import { bothWorth, earlyBonusPct, packValue } from '../sim/economy';
import { getLevel } from '../content/levels';
import { computeFlow } from '../sim/flow';
import { degree, dist, lineById, nodeById, slotsFree } from '../sim/grid';
import { cardChoice, cityReserve, powerChoice, tripSoon } from './coach';
import { UFLS_CHZ } from '../sim/threats';
import { hzZone } from '../sim/hz';
import { cloneState, createState } from '../sim/state';
import { step } from '../sim/step';
import type { Candidate, DifficultyId, GridNode, Intent, PlayerId, SimEvent, State } from '../sim/types';

export interface Metrics {
  /** MW som byerne mangler (blackout-pausen tæller med, som om byen var tændt). */
  unservedMw: number;
  /** Byer med mindst 90 % leverance. */
  powered: number;
  greenMw: number;
  servedMw: number;
  shields: number;
  budget: number;
}

export interface ScoredCandidate {
  cand: Candidate;
  score: number;
  prevent: number;
  green: number;
  income: number;
  price: number;
}

function hypothetical(state: State): State {
  const s = cloneState(state);
  for (const n of s.nodes) if (n.city) n.city.darkTicks = 0;
  return s;
}

/** Mål et net: hvor meget mangler, hvor grønt, hvor robust. Regner flowet på en kopi. */
export function metrics(state: State, alreadyCloned = false): Metrics {
  const s = alreadyCloned ? state : hypothetical(state);
  computeFlow(s);
  let unserved = 0;
  let powered = 0;
  let shields = 0;
  for (const n of s.nodes) {
    if (!n.city || !n.active || n.phantom) continue;
    const short = Math.max(0, n.city.demandMw - n.city.servedMw);
    unserved += short;
    if (n.city.demandMw <= 0 || n.city.servedMw * 1000 >= n.city.demandMw * 900) powered++;
    shields += n.shield;
  }
  return { unservedMw: unserved, powered, greenMw: s.flow.greenMw, servedMw: s.flow.servedMw, shields, budget: s.budget };
}

function noise(state: State, id: string): number {
  // FNV-1a over seed, tick og kandidat: ±5.
  let h = 0x811c9dc5 ^ state.seed;
  const key = `${state.tick}:${id}`;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return ((h % 1001) / 100) - 5;
}

/** Hvor meget mangler der, hvis ledningen falder ud? */
function lossIfBroken(state: State, base: Metrics, lineId: string): number {
  const s = hypothetical(state);
  const line = lineById(s, lineId);
  if (!line) return 0;
  line.status = 'broken';
  const m = metrics(s, true);
  return Math.max(0, m.powered < base.powered ? base.powered - m.powered : 0) + Math.max(0, m.unservedMw - base.unservedMw) / 200;
}

function scoreOf(prevent: number, green: number, income: number, price: number): number {
  return 100 * prevent + 20 * green + 0.3 * income - 0.5 * price;
}

/** Vurdér én kandidat. */
export function scoreCandidate(state: State, base: Metrics, cand: Candidate, recent: ReadonlySet<string> = new Set()): ScoredCandidate {
  let prevent = 0;
  let green = 0;
  let income = 0;
  let price = cand.cost;
  let extra = 0;

  switch (cand.kind) {
    case 'wait':
      break;
    case 'dispatch_crew': {
      const s = hypothetical(state);
      const line = lineById(s, cand.target ?? '');
      if (line) {
        line.status = 'ok';
        line.halfDown = false;
      }
      const m = metrics(s, true);
      prevent = Math.max(0, m.powered - base.powered) + Math.max(0, base.unservedMw - m.unservedMw) / 200;
      extra = 30; // Reparationer er altid gode: skjolde og stormsikkerhed.
      break;
    }
    case 'reinforce_mast': {
      // "Forstærk alle" er spillerens genvej; bots og Volt vælger de enkelte master selv.
      if (cand.target === '*') {
        extra = -100;
        break;
      }
      const t = state.threats.find((x) => x.kind === 'storm' && x.phase === 'warning' && x.atRisk.includes(cand.target ?? ''));
      const p = t ? Math.min(1, t.strength / Math.max(1, t.atRisk.length)) : 0;
      prevent = p * lossIfBroken(state, base, cand.target ?? '');
      extra = p > 0 ? 3 : 0;
      break;
    }
    case 'shoo_threat': {
      const t = state.threats.find((x) => x.id === cand.target);
      prevent = t ? lossIfBroken(state, base, t.targetLine) : 0;
      extra = 25;
      break;
    }
    case 'answer_card': {
      const t = state.threats.find((x) => x.id === cand.target);
      extra = t && cardChoice(state, t) === cand.opt ? 60 : -20;
      break;
    }
    case 'power_fcr':
    case 'power_shed':
    case 'power_norway': {
      const p = powerChoice(state);
      extra = p && cand.kind === `power_${p.power}` && (p.target ?? undefined) === cand.target ? 90 : -100;
      break;
    }
    case 'build_flywheel': {
      // Svinghjulet hjælper kun, når et værk snart ryger og frekvensen ellers falder under grænsen.
      const t = tripSoon(state);
      const n = nodeById(state, cand.target ?? '');
      const saves = !!t && !!n && n.area === t.area && t.nadir < UFLS_CHZ;
      prevent = saves ? 1 : 0;
      extra = saves ? 20 + (cand.target === t?.flywheelNode ? 10 : 0) : -60;
      break;
    }
    case 'hire_crew': {
      const broken = state.lines.filter((l) => l.status === 'broken' || l.halfDown).length;
      const idle = state.crews.filter((c) => c.kind === 'van' && c.task === 'idle').length;
      const vans = state.crews.filter((c) => c.kind === 'van').length;
      extra = broken > idle ? 40 : vans < 2 && state.budget > 40 ? 6 : -10;
      break;
    }
    case 'upgrade_station': {
      const n = nodeById(state, cand.target ?? '');
      const short = n?.city ? Math.max(0, n.city.demandMw - n.city.servedMw) : 0;
      extra = n && slotsFree(state, n) <= 0 && (short > 0 || n.kind === 'station') ? 10 + short / 20 : -10;
      break;
    }
    case 'demolish':
    case 'bury_line':
      extra = -1000; // Botten river ikke ned og graver ikke ned.
      break;
    case 'trim_hz': {
      // Frekvensnålen (hz.ts): regulér, når den er ude af det grønne; over 200 mHz koster den penge.
      const zone = hzZone(state.hz?.dev ?? 0);
      extra = zone === 'orange' ? 70 : zone === 'yellow' ? 30 : -100;
      break;
    }
    case 'call_year':
      // Næste år er kun lovligt, når året er spillet og nettet er roligt: en god idé, når intet andet haster.
      extra = 10 + earlyBonusPct(state) / 2;
      break;
    case 'pick_pack': {
      // Samme vurdering som automatvalget (economy.packValue), så botten og uret vælger ens.
      const pack = state.packOffer[Number(cand.opt)];
      extra = pack ? 50 + packValue(state, getLevel(state.levelId), pack) : 0;
      break;
    }
    case 'pick_both': {
      // Begge gaver: over den bedste enkelte gave, når begge kan bruges og kassen har en reserve
      // (economy.bothWorth). Prisen trækkes fra i scoren som alt andet; her lægges den til igen.
      const level = getLevel(state.levelId);
      const best = Math.max(...state.packOffer.map((p) => packValue(state, level, p)));
      extra = bothWorth(state, level) ? 60 + best + 0.5 * cand.cost : -1000;
      break;
    }
    default: {
      // Byggeri: klon, udfør, regn flow.
      const s = hypothetical(state);
      const r = apply(s, { player: 'p2', clientSeq: Number.MAX_SAFE_INTEGER, kind: cand.kind, target: cand.target, opt: cand.opt, source: 'bot' } as Intent);
      if (!r.ok) {
        extra = -50;
        break;
      }
      const m = metrics(s, true);
      prevent = Math.max(0, m.powered - base.powered) + Math.max(0, base.unservedMw - m.unservedMw) / 200;
      green = (m.greenMw - base.greenMw) / 100;
      income = ((m.servedMw - base.servedMw) / 100) * 2;
      price = state.budget - s.budget;
      // Skjolde: en anden vej til en by er lidt værd, også uden underskud.
      extra = Math.max(0, m.shields - base.shields) * 4;
      break;
    }
  }
  if (recent.has(cand.id)) extra -= 30;
  // Ikke de sidste kroner på sikkerhed eller kapacitet, når en ny by er lige på vej.
  if ((cand.kind === 'reinforce_mast' || cand.kind === 'upgrade_line' || cand.kind === 'build_battery') && state.budget - price < cityReserve(state)) extra -= 80;
  const score = scoreOf(prevent, green, income, price) + extra + noise(state, cand.id);
  return { cand, score, prevent, green, income, price };
}

/** Højst så mange byggekandidater regnes igennem med flow pr. beslutning. */
export const EVAL_BUILDS = 12;
export const EVAL_UPGRADES = 4;
export const EVAL_BATTERIES = 3;

/** Hvor meget en node trænger: by med underskud, producent med ledig kraft, eller helt uden ledning. */
function need(state: State, n: GridNode | undefined): number {
  if (!n || !n.active || n.phantom) return 0;
  const lone = degree(state, n.id) === 0 ? 4 : 0;
  if (n.city) return Math.max(0, n.city.demandMw - n.city.servedMw) / 40 + lone + (n.shield <= 1 ? 1 : 0);
  if (n.producer) return Math.max(0, n.producer.availMw - n.producer.usedMw) / 60 + lone;
  return degree(state, n.id) > 0 ? 0.5 : 0;
}

/** Billig forhåndsvurdering af en dyr kandidat; højere er mere lovende. */
function cheap(state: State, c: Candidate): number {
  if (c.kind === 'build_line') {
    const [a, b] = (c.target ?? '').split('|').map((id) => nodeById(state, id));
    if (!a || !b) return -Infinity;
    const medium = c.opt === 'air' ? 0 : c.opt === 'sea' ? 1 : 2;
    return need(state, a) + need(state, b) - dist(a, b) / 25 - medium - c.cost / 10;
  }
  if (c.kind === 'upgrade_line') return lineById(state, c.target ?? '')?.loadPm ?? 0;
  if (c.kind === 'build_battery') return need(state, nodeById(state, c.target ?? ''));
  return 0;
}

const LIMIT: Partial<Record<Candidate['kind'], number>> = {
  build_line: EVAL_BUILDS,
  upgrade_line: EVAL_UPGRADES,
  build_battery: EVAL_BATTERIES,
};

/** Skær de dyre kandidater ned til de mest lovende pr. slags. Rækkefølgen af resten bevares. */
export function prefilter(state: State, legal: Candidate[]): Candidate[] {
  const keep = new Set<Candidate>();
  for (const kind of Object.keys(LIMIT) as Candidate['kind'][]) {
    const limit = LIMIT[kind] ?? 0;
    const of = legal.filter((c) => c.kind === kind);
    if (of.length <= limit) {
      for (const c of of) keep.add(c);
      continue;
    }
    of.map((c) => ({ c, v: cheap(state, c) }))
      .sort((x, y) => y.v - x.v || (x.c.id < y.c.id ? -1 : 1))
      .slice(0, limit)
      .forEach((x) => keep.add(x.c));
  }
  return legal.filter((c) => LIMIT[c.kind] === undefined || keep.has(c));
}

/** Rangér de lovlige handlinger (efter forhåndsfiltret). `wait` er altid med og har score 0 plus støj. */
export function rank(state: State, player: PlayerId = 'p2', recent: ReadonlySet<string> = new Set()): ScoredCandidate[] {
  const base = metrics(state);
  // Nedrivning er spillerens fortryd-knap, ikke et træk for botten.
  const legal = legalActions(state, player).filter((c) => c.kind !== 'demolish' && c.target !== '*');
  const out = prefilter(state, legal).map((c) => scoreCandidate(state, base, c, recent));
  out.sort((a, b) => b.score - a.score || (a.cand.id < b.cand.id ? -1 : 1));
  return out;
}

/** Bottens valg lige nu, eller null for "vent". */
export function choose(state: State, player: PlayerId = 'p2', recent: ReadonlySet<string> = new Set()): Candidate | null {
  const ranked = rank(state, player, recent);
  const best = ranked[0];
  if (!best || best.cand.kind === 'wait' || best.score <= 5) return null;
  return best.cand;
}

export interface HeadlessResult {
  state: State;
  actions: number;
  events: SimEvent[];
}

export interface HeadlessOptions {
  /** Ticks mellem bottens beslutninger (standard 15 = 1,5 s, som Volts tempo). */
  every?: number;
  /** Botten er slået fra: en passiv spiller. */
  passive?: boolean;
  /** Stop efter så mange ticks (sikring). */
  maxTicks?: number;
  /** Gem hændelser (kan være mange). */
  keepEvents?: boolean;
  /** Hvilken spiller botten er (standard p1). */
  player?: PlayerId;
}

/** Spil en hel bane med botten (eller passivt) uden skærm. */
export function playHeadless(levelId: string, diff: DifficultyId, seed: number, opts: HeadlessOptions = {}): HeadlessResult {
  const state = createState(levelId, diff, seed);
  const player = opts.player ?? 'p1';
  const every = opts.every ?? 15;
  const maxTicks = opts.maxTicks ?? 20_000;
  const events: SimEvent[] = [];
  let seq = 0;
  let actions = 0;
  const recent: string[] = [];
  for (let i = 0; i < maxTicks && state.phase !== 'won' && state.phase !== 'lost'; i++) {
    const intents: Intent[] = [];
    if (!opts.passive && state.tick % every === 0) {
      const c = choose(state, player, new Set(recent));
      if (c) {
        const intent: Intent = { player, clientSeq: ++seq, kind: c.kind, source: 'bot' };
        if (c.target !== undefined) intent.target = c.target;
        if (c.opt !== undefined) intent.opt = c.opt;
        intents.push(intent);
        actions++;
        recent.push(c.id);
        if (recent.length > 4) recent.shift();
      }
    }
    const ev = step(state, intents);
    if (opts.keepEvents) for (const e of ev) events.push(e);
  }
  return { state, actions, events };
}
